import { useEffect } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

import {
  UseMutateAsyncFunction,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { dequal } from 'dequal';

import { TableView } from '@components/table';
import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';
import { storage } from '@utils/browser';
import { minutes } from '@utils/milliseconds';

import { useWebSocket } from 'api/hooks';
import { handleError } from 'api/query-client';
import { removeDebugToken } from 'api/request';
import { routePaths } from 'ui/authenticated/navigation';

import {
  CompanyUserOptions,
  createUserApp,
  CurrentUser,
  deleteUserApp,
  getCurrentUser,
  loadUserApps,
  login,
  loginAzure,
  logout,
  switchCompany,
  updateUserApp,
  updateUserOptions,
  UserApp,
  UserAppResponse,
} from './requests';

const currentUserQueryKey = ['authentication', 'currentUser'];

export const useCurrentUserQuery = () => {
  return useQuery({
    queryKey: currentUserQueryKey,
    queryFn: getCurrentUser,
    staleTime: Infinity,
    retryOnMount: false,
    refetchInterval: minutes(10),
    refetchOnWindowFocus: 'always',
  });
};

export const useCurrentUser = () => {
  const query = useCurrentUserQuery();

  return query.data;
};

export const useCurrentCompany = () => {
  const currentUser = useCurrentUser();
  return currentUser?.company;
};

export const useIsOutOfCredits = () => {
  const currentUser = useCurrentUser();

  return Boolean(
    currentUser?.plan && currentUser.plan.creditsUsed >= currentUser.plan.creditsPerPeriod
  );
};

export const usePlan = () => {
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  const company = useCurrentCompany();
  const enabled = Boolean(company?.shortId);
  useWebSocket(`plans/${company?.shortId}/`, invalidateCurrentUser, {
    throttleTime: 10000,
    enabled,
  });
  const currentUser = useCurrentUser();
  return currentUser?.plan;
};

export const useTableView = (tableName: string) => {
  const { value, update } = useUserOption(`tableViewFor${tableName}`, null);
  return {
    tableView: value,
    setTableView: update.mutateAsync as UseMutateAsyncFunction<any, unknown, TableView, unknown>,
  };
};

export const useUserOption = <K extends keyof CompanyUserOptions>(key: K, defaultValue: any) => {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  const update = useMutation(
    (value: CompanyUserOptions[K]) => {
      queryClient.setQueryData(currentUserQueryKey, (current: CurrentUser | undefined) => {
        if (current?.options) {
          current.options[key] = value;
          return { ...current };
        }
      });
      return updateUserOptions({ key, value });
    },
    {
      onSuccess: invalidateCurrentUser,
    }
  );
  return { update, value: currentUser?.options?.[key] ?? defaultValue };
};

type LoginErrorResponse = AxiosError<{
  nonFieldErrors?: string[];
  email?: string[];
  password?: string[];
}>;

export type LoginErrors = {
  email?: string;
  password?: string;
};

export const useLogin = (setErrors: (errors: LoginErrors) => void, path?: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  const navigate = useNavigate();
  const location = useLocation();

  return useMutation({
    mutationFn: login,
    onSuccess: async () => {
      await invalidateCurrentUser();
      if (path) {
        setTimeout(() => {
          navigate(path, { state: { from: location } });
        });
      }
    },
    onError: (data: LoginErrorResponse) => {
      handleError(() => {
        if (data?.response?.data?.nonFieldErrors) {
          setErrors({ password: data.response.data.nonFieldErrors.join(', ') });
        } else {
          if (data?.response?.data?.email || data?.response?.data?.password) {
            const emailError = data?.response?.data?.email
              ? data.response.data.email.join(', ')
              : undefined;
            const passwordError = data?.response?.data?.password
              ? data.response.data.password.join(', ')
              : undefined;
            setErrors({ email: emailError, password: passwordError });
          } else {
            setErrors({ password: t('unknownError') });
          }
        }
      });
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      removeDebugToken();
      queryClient.resetQueries({ queryKey: currentUserQueryKey, exact: true });
      queryClient.removeQueries({
        predicate: (query) => {
          return !dequal(query.queryKey, currentUserQueryKey);
        },
      });
    },
  });
};

export const useLoginAzure = () => {
  return useMutation({
    mutationFn: loginAzure,
  });
};

const userAppsQueryKey = ['user-apps'];

export const useUserApps = () => {
  return useQuery({
    queryKey: userAppsQueryKey,
    queryFn: () => loadUserApps(),
  });
};

export const useInvalidateAuthentication = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentUser = () => queryClient.invalidateQueries(currentUserQueryKey);
  const invalidateUserApps = () => queryClient.invalidateQueries(userAppsQueryKey);

  return { invalidateCurrentUser, invalidateUserApps };
};

export const useCreateUserApp = (
  options?: UseMutationOptions<UserAppResponse, unknown, Omit<UserApp, 'clientId'>>
) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateUserApps } = useInvalidateAuthentication();

  return useMutation({
    ...options,
    mutationFn: createUserApp,
    onSuccess: (...data) => {
      invalidateUserApps();
      showSuccess(t('userAppCreated'));
      options?.onSuccess?.(...data);
    },
    onError: () => {
      handleError(t('userAppNotCreated'));
    },
  });
};

export const useUpdateUserApp = (options?: UseMutationOptions<UserApp, unknown, UserApp>) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateUserApps } = useInvalidateAuthentication();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: updateUserApp,
    onSuccess: (...data) => {
      invalidateUserApps();
      queryClient.invalidateQueries(['user-app']);
      showSuccess(t('userAppUpdated'));
      options?.onSuccess?.(...data);
    },
    onError: () => {
      handleError(t('userAppNotUpdated'));
    },
  });
};

export const useDeleteUserApp = (options?: UseMutationOptions<void, unknown, unknown>) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateUserApps } = useInvalidateAuthentication();

  return useMutation(deleteUserApp, {
    ...options,
    onSuccess(...data) {
      invalidateUserApps();
      showSuccess(t('userAppDeleted'));
      options?.onSuccess?.(...data);
    },
    onError: () => {
      handleError(t('userAppNotDeleted'));
    },
  });
};

export const useSwitchCompany = () => {
  const queryClient = useQueryClient();
  const { t } = useScopedTranslate(locale);
  const navigate = useNavigate();

  let switchTimeout: NodeJS.Timeout;

  useEffect(() => {
    return () => {
      clearTimeout(switchTimeout);
    };
  });
  return useMutation(switchCompany, {
    onSuccess(_, variables) {
      queryClient.resetQueries({ queryKey: currentUserQueryKey, exact: true });
      queryClient.removeQueries({
        predicate: (query) => {
          return !dequal(query.queryKey, currentUserQueryKey);
        },
      });
      const currentWorkspace = storage.get('current-workspace-' + variables.shortId);
      const defaultRoute = currentWorkspace
        ? generatePath(routePaths.workspaceJourneyList, {
            workspaceId: currentWorkspace,
          })
        : routePaths.travellersList;
      navigate(defaultRoute, { replace: true });

      switchTimeout = setTimeout(() => {
        // Timeout so it transitions over to the new company before showing,
        // that way it stops freezing on screen when the toaster component unmounts.
        showSuccess(t('successfullySwitched', { companyName: variables.name }));
      }, 300);
    },
    onError: () => {
      handleError(t('switchUnsuccessful'));
    },
  });
};

const locale = {
  en: {
    unknownError: 'Unknown error',
    userAppCreated: 'API key created',
    userAppNotCreated: 'Unable to create API key',
    userAppUpdated: 'API key updated',
    userAppNotUpdated: 'Unable to update API key',
    userAppDeleted: 'API key removed',
    userAppNotDeleted: 'Unable to remove API key',
    successfullySwitched: 'Successfully switched company to {companyName}',
    switchUnsuccessful: 'Switching companies was unsuccessful',
  },
};
